<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { placesAutocomplete } from '../lib/api';
  import { placeIcon } from '../lib/address';
  import { getCurrentMap } from '../lib/maps/map';
  import type { Prediction } from '../lib/types';

  export let value = '';
  export let placeholder = '';
  export let ariaLabel = '';
  export let nameOnly = false;

  const dispatch = createEventDispatcher<{
    select: { description: string; prediction: Prediction };
    submit: { value: string };
  }>();

  let items: Prediction[] = [];
  let activeIdx = -1;
  let sessionToken = newToken();
  let seq = 0;
  let debounceTimer: ReturnType<typeof setTimeout> | undefined;
  let selected: Prediction | null = null;
  const listId = `ac-list-${Math.random().toString(36).slice(2, 8)}`;

  function newToken(): string {
    return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.floor(Math.random() * 16);
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  // Split main_text into highlighted/plain segments (no @html needed).
  function segments(text: string, matches?: { offset: number; length: number }[]) {
    if (!matches || !matches.length) return [{ text, bold: false }];
    const out: { text: string; bold: boolean }[] = [];
    let pos = 0;
    for (const m of matches) {
      if (m.offset > pos) out.push({ text: text.slice(pos, m.offset), bold: false });
      out.push({ text: text.slice(m.offset, m.offset + m.length), bold: true });
      pos = m.offset + m.length;
    }
    if (pos < text.length) out.push({ text: text.slice(pos), bold: false });
    return out;
  }

  async function query(text: string) {
    const my = ++seq;
    const opts: { sessiontoken: string; location?: string; radius?: string } = { sessiontoken: sessionToken };
    const map = getCurrentMap();
    const c = map?.getCenter();
    if (c) { opts.location = `${c.lat()},${c.lng()}`; opts.radius = '30000'; }
    const preds = await placesAutocomplete(text, opts);
    if (my !== seq) return;
    items = preds;
    activeIdx = -1;
  }

  function onInput() {
    selected = null;
    const t = value.trim();
    clearTimeout(debounceTimer);
    if (t.length < 3) { items = []; return; }
    debounceTimer = setTimeout(() => query(t), 220);
  }

  function choose(i: number) {
    const p = items[i];
    if (!p) return;
    value = nameOnly
      ? (p.structured_formatting?.main_text || p.description?.split(',')[0] || '')
      : (p.description || '');
    selected = p;
    items = [];
    sessionToken = newToken();
    dispatch('select', { description: p.description || value, prediction: p });
  }

  function onKeydown(e: KeyboardEvent) {
    const open = items.length > 0;
    if (e.key === 'ArrowDown' && open) { e.preventDefault(); activeIdx = (activeIdx + 1) % items.length; }
    else if (e.key === 'ArrowUp' && open) { e.preventDefault(); activeIdx = (activeIdx - 1 + items.length) % items.length; }
    else if (e.key === 'Enter') {
      if (open && activeIdx >= 0) { e.preventDefault(); choose(activeIdx); }
      else { items = []; dispatch('submit', { value }); }
    } else if (e.key === 'Escape' && open) { e.preventDefault(); items = []; }
  }

  export function getPrediction(): Prediction | null {
    return selected;
  }
  export function reset() {
    value = '';
    selected = null;
    items = [];
  }
</script>

<div class="ac-wrap">
  <input
    type="text"
    bind:value
    {placeholder}
    aria-label={ariaLabel}
    autocomplete="off"
    role="combobox"
    aria-autocomplete="list"
    aria-controls={listId}
    aria-expanded={items.length > 0}
    on:input={onInput}
    on:keydown={onKeydown}
    on:blur={() => setTimeout(() => (items = []), 120)}
  />
  {#if items.length}
    <div class="ac-dropdown" id={listId} role="listbox">
      {#each items as p, i (p.place_id ?? i)}
        <!-- mousedown (not click) so it fires before the input's blur closes us -->
        <div
          class="ac-item"
          class:active={i === activeIdx}
          role="option"
          tabindex="-1"
          aria-selected={i === activeIdx}
          on:mousedown|preventDefault={() => choose(i)}
        >
          <span class="ac-thumb">{placeIcon(p.types)}</span>
          <span class="ac-text">
            <div class="ac-main">
              {#if p.structured_formatting?.main_text}
                {#each segments(p.structured_formatting.main_text, p.structured_formatting.main_text_matched_substrings) as seg}
                  {#if seg.bold}<b>{seg.text}</b>{:else}{seg.text}{/if}
                {/each}
              {:else}{p.description}{/if}
            </div>
            {#if p.structured_formatting?.secondary_text}
              <div class="ac-sub">{p.structured_formatting.secondary_text}</div>
            {/if}
          </span>
        </div>
      {/each}
      <div class="ac-attribution">Powered by Google</div>
    </div>
  {/if}
</div>
